/**
 * This module contains API functions for Equipment management.
 */
import { CreateEquipmentData, UpdateEquipmentData } from '../hooks/useEquipment/types';
import { EQUIPMENT_CACHE_KEY, EQUIPMENT_DETAIL_CACHE_KEY } from './queryKeys';

// Re-export query keys for convenience
export const EQUIPMENT_QUERY_KEYS = {
  list: EQUIPMENT_CACHE_KEY,
  detail: EQUIPMENT_DETAIL_CACHE_KEY,
};

/**
 * Get equipment list request
 */
export function getEquipmentRequest(organisationId: string): { path: string; body?: Record<string, unknown> } {
  return {
    path: `/organisations/${organisationId}/equipments/`,
  };
}

/**
 * Get rental batteries from Greener Power Solution catalog
 * This endpoint returns batteries available for rental from the main organization
 */
export function getRentalBatteriesRequest(): { path: string; body?: Record<string, unknown> } {
  return {
    path: `/organisations/equipments/rental-batteries`,
  };
}

/**
 * Get single rental battery item request
 */
export function getRentalBatteryDetailRequest(
  batteryId: string
): { path: string; body?: Record<string, unknown> } {
  return {
    path: `/organisations/equipments/rental-batteries/${batteryId}`,
  };
}

/**
 * Get single equipment item request
 */
export function getEquipmentDetailRequest(
  equipmentId: string
): { path: string; body?: Record<string, unknown> } {
  return {
    path: `/organisations/equipments/${equipmentId}`,
  };
}

/**
 * Helper function to create FormData for create equipment requests
 */
function prepareCreateEquipmentFormData(data: CreateEquipmentData): FormData {
  const formData = new FormData();

  const equipmentData = {
    name: data.name,
    kva_value: data.kva_value,
    kwh_capacity: data.kwh_capacity,
    kw_power: data.kw_power,
    description: data.description,
    equipment_type: data.equipment_type,
  };
  formData.append('equipment', JSON.stringify(equipmentData));

  if (data.imageFile) {
    formData.append('image', data.imageFile);
  } else {
    // Send empty file if somehow no image provided
    formData.append('image', new Blob(), '');
  }

  return formData;
}

/**
 * Helper function to create FormData for update equipment requests
 */
function prepareUpdateEquipmentFormData(data: UpdateEquipmentData): FormData {
  const formData = new FormData();

  const equipmentData = {
    name: data.name,
    kva_value: data.kva_value,
    kwh_capacity: data.kwh_capacity,
    kw_power: data.kw_power,
    description: data.description,
    equipment_type: data.equipment_type,
  };
  formData.append('equipment_update', JSON.stringify(equipmentData));

  // Only append image if it's provided and has content
  if (data.imageFile && data.imageFile.size > 0) {
    formData.append('image', data.imageFile);
  }

  return formData;
}

/**
 * Create equipment request
 */
export function createEquipmentRequest(
  organisationId: string,
  data: CreateEquipmentData
): { path: string; body: FormData } {
  return {
    path: `/organisations/${organisationId}/equipments/`,
    body: prepareCreateEquipmentFormData(data),
  };
}

/**
 * Update equipment request
 */
export function updateEquipmentRequest(
  equipmentId: string,
  data: UpdateEquipmentData
): { path: string; body: FormData } {
  return {
    path: `/organisations/equipments/${equipmentId}`,
    body: prepareUpdateEquipmentFormData(data),
  };
}

/**
 * Delete equipment request
 */
export function deleteEquipmentRequest(
  equipmentId: string
): { path: string; body?: Record<string, unknown> } {
  return {
    path: `/organisations/equipments/${equipmentId}`,
  };
}

/**
 * Upload equipment image request
 */
export function uploadEquipmentImageRequest(
  organisationId: string,
  equipmentId: string,
  file: File
): { path: string; body: FormData } {
  const formData = new FormData();
  formData.append('image', file);

  return {
    path: `/organisations/${organisationId}/equipments/${equipmentId}/image/`,
    body: formData,
  };
}
